import React, { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import {
  findPartnerByNumber,
  getAllCategories,
  getAllCompanies,
  getStateAndCity
} from '../../services/api/implementation/impl';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Multiselect from 'multiselect-react-dropdown';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { capitalizeWords } from '../../utils/stringUtils';
// Define a regex for the pincode validation (6 digits)
const pincodeRegex = /^[0-9]{6}$/;
const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const noSpaceRegex = /^\S*$/; // No spaces allowed
const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const validationSchema = yup.object().shape({
  partner_type: yup
    .string()
    .oneOf(['individual', 'company'], 'Invalid type')
    .required('Type is required'),
  name: yup.string().when('partner_type', {
    is: 'individual',
    then: (schema) =>
      schema
        .required('Full name is required')
        .min(3, 'Full name should be at least 3 characters')
        .max(50, 'Full name should be at most 50 characters'),
    otherwise: (schema) => schema.notRequired()
  }),
  businessName: yup.string().when('partner_type', {
    is: 'company',
    then: (schema) =>
      schema
        .required('Business name is required')
        .max(100, 'Business name should be at most 100 characters'),
    otherwise: (schema) => schema.notRequired()
  }),
  phone_number: yup
    .string()
    .required('Phone number is required')
    .matches(/^[0-9]{10}$/, 'Invalid phone number'),
  email: yup.string().required('Email is required').email('Invalid email format'),
  zip_code: yup
    .string()
    .required('Zip/Pin code is required')
    .matches(pincodeRegex, 'Zip/Pin code should be exactly 6 digits'),
  country: yup.string().required('Country is required').oneOf(['India'], 'Country must be India'),
  state: yup.string().required('State is required'),
  city: yup.string().required('City is required'),
  pan: yup
    .string()
    .required('PAN is required')
    .matches(panRegex, 'Invalid PAN format')
    .matches(noSpaceRegex, 'PAN should not contain spaces'),
  aadhar: yup.string().when('partner_type', {
    is: 'individual',
    then: (schema) => schema.required('Aadhar number is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  gst: yup.string().when('partner_type', {
    is: 'company',
    then: (schema) => schema.required('GST is required').matches(gstRegex, 'Invalid GST format'),
    otherwise: (schema) => schema.notRequired()
  }),
  company_ids: yup.array().min(1, 'At least one company must be selected')
});

export const AddPartner = ({
  onClose,
  onSubmit,
  isSubmitting,
  modalType,
  selectedVendor,
  setModalType,
  setIsSubmitting
}) => {
  const userId = Cookies.get('userId');
  // Local state for address details
  const [categories, setCategories] = useState([]);
  const [city, setCity] = useState('Select');
  const [state, setState] = useState('-');
  const [cityDropdownOptions, setCityDropdownOptions] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
    getValues,
    formState,
    watch
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { partner_type: 'individual' }
  });

  const partnerType = watch('partner_type');

  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: getAllCompanies
  });

  // Fetch vendor categories
  const { data: allCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: getAllCategories
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Add Partner Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (allCategories?.data && allCategories.data.length > 0) {
      setCategories(allCategories.data);
    }
  }, [allCategories]);

  // Handle form submission
  const onSubmitForm = (data) => {
    if (!data.company_ids || data.company_ids.length < 0) {
      toast.error('Please select company');
      return;
    }
    const formattedData = {
      name: data.partner_type === 'company' ? data.businessName : data.name,
      businessName: data.partner_type === 'individual' ? data.name : data.businessName,
      phone_number: data.phone_number,
      email: data.email,
      pan: data.pan,
      adhaar_no: data.partner_type === 'individual' ? data.aadhar : undefined,
      state: data.state,
      gst: data.partner_type === 'company' ? data.gst : undefined,
      company_ids: data.company_ids?.map((item) => item.id),
      type: data.partner_type,
      pincode: data.zip_code
    };

    Object.keys(formattedData).forEach(
      (key) => formattedData[key] === undefined && delete formattedData[key]
    );

    onSubmit(formattedData);
    reset();
  };

  const transformedDataCompany = companies?.map((item) => ({
    name: item.company_name?.trim(),
    id: item.id
  }));

  // Input handlers for sanitizing values
  const handleNameChange = useCallback(
    (val) => {
      const cleanedValue = val.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
      setValue('name', capitalizeWords(cleanedValue));
    },
    [setValue]
  );

  const handleBusinessNameChange = useCallback(
    (val) => {
      const cleanedValue = val.replace(/[^a-zA-Z\s]/g, '').slice(0, 100);
      setValue('businessName', capitalizeWords(cleanedValue));
    },
    [setValue]
  );

  const handlePhoneChange = useCallback(
    (val) => {
      const cleanedValue = val.replace(/[^0-9]/g, '').slice(0, 10);
      if (cleanedValue.length === 10) {
        FindExisitingPartner(cleanedValue);
      }
      setValue('phone_number', cleanedValue);
    },
    [setValue]
  );

  const handlePanChange = useCallback(
    async (pan) => {
      const cleanedPan = pan.toUpperCase().replace(/[^a-zA-Z0-9]/g, '');
      setValue('pan', cleanedPan);
    },
    [setValue]
  );

  const handleAadharChangeFirst = useCallback(
    (val) => {
      let cleaned = val.replace(/\D/g, '');
      if (cleaned.length > 12) cleaned = cleaned.slice(0, 12);
      setValue('aadhar', cleaned);
    },
    [setValue]
  );

  const handleGSTChange = useCallback(
    (val) => {
      const cleaned = val
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 15);
      setValue('gst', cleaned);
    },
    [setValue]
  );

  // When a valid 6-digit pincode is entered, fetch the state and city details
  const handlePincodeChange = useCallback(
    async (pincode) => {
      const cleanedPincode = pincode.replace(/[^0-9]/g, '');
      setValue('zip_code', cleanedPincode);

      if (cleanedPincode.length === 6) {
        try {
          const pincodeList = await getStateAndCity(cleanedPincode);
          const postOfficeList = pincodeList[0].PostOffice;

          if (postOfficeList.length > 0) {
            const newState = postOfficeList[0].State;
            const uniqueCircles = [
              ...new Set(
                postOfficeList
                  .map((office) => {
                    const circle =
                      office.Name && office.Name !== 'NA' && office.Name.trim() !== ''
                        ? office.Name
                        : office.Block;
                    return circle && circle !== 'NA' && circle.trim() !== '' ? circle : null;
                  })
                  .filter(Boolean)
              )
            ];
            const uniqueTehsils = [...new Set(postOfficeList.map((office) => office.Block))];

            // Set dropdown options for city (or tehsil)
            setCityDropdownOptions(
              uniqueTehsils.map((tehsil) => ({ value: tehsil, label: tehsil }))
            );
            const newCity = uniqueCircles[0];

            setValue('state', newState);
            setValue('city', uniqueTehsils[0]);

            setState(newState);
            setCity(newCity);
          } else {
            setValue('state', '-');
            setValue('city', 'Select');
            setCityDropdownOptions([]);
            setState('-');
            setCity('Select');
          }
        } catch (error) {
          toast.error('The entered pincode is invalid. Please enter a valid pincode.');
          setValue('state', '-');
          setValue('city', 'Select');
          setCityDropdownOptions([]);
          setState('-');
          setCity('Select');
        }
      }
    },
    [setValue]
  );

  const { mutate: handleFindExisitingPartner } = useMutation({
    mutationFn: (payload) => findPartnerByNumber(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (data) => {
      setIsSubmitting(false);
      if (data) {
        setValue('name', data.user?.name || '');
        setValue('businessName', data.businessName || '');
        setValue('email', data.user?.email || '');
        setValue('phone_number', data.user?.phone_number || '');
        setValue('zip_code', data.pincode || '');
        setValue('pan', data.pan || '');
        setValue('aadhar', data.adhaar_no || '');
        setValue('gst', data.gst || '');
        setValue(
          'partner_type',
          data?.type === 'company' || data?.type === 'individual'
            ? data.type
            : data?.gst
              ? 'company'
              : 'individual'
        );

        if (data.pincode) {
          try {
            const pincodeList = await getStateAndCity(data.pincode);
            const postOfficeList = pincodeList[0].PostOffice;

            if (postOfficeList.length > 0) {
              const newState = postOfficeList[0].State;
              const uniqueCircles = [
                ...new Set(
                  postOfficeList
                    .map((office) => {
                      const circle =
                        office.Name && office.Name !== 'NA' && office.Name.trim() !== ''
                          ? office.Name
                          : office.Block;
                      return circle && circle !== 'NA' && circle.trim() !== '' ? circle : null;
                    })
                    .filter(Boolean)
                )
              ];
              const uniqueTehsils = [...new Set(postOfficeList.map((office) => office.Block))];

              // Set dropdown options for city (or tehsil)
              setCityDropdownOptions(
                uniqueTehsils.map((tehsil) => ({ value: tehsil, label: tehsil }))
              );
              const newCity = uniqueCircles[0];

              setValue('state', newState);
              setValue('city', uniqueTehsils[0]);

              setState(newState);
              setCity(newCity);
            } else {
              setValue('state', '-');
              setValue('city', 'Select');
              setCityDropdownOptions([]);
              setState('-');
              setCity('Select');
            }
          } catch (error) {
            toast.error('The entered pincode is invalid. Please enter a valid pincode.');
            setValue('state', '-');
            setValue('city', 'Select');
            setCityDropdownOptions([]);
            setState('-');
            setCity('Select');
          }
        }
      }
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error finding partner', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error finding partner');
    }
  });

  const FindExisitingPartner = (num) => {
    const payload = {
      phone_number: num
    };
    handleFindExisitingPartner(payload);
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-3">
      {/* Vendor Information */}
      <div className="flex justify-between items-center w-full">
        <FormInput
          width={48}
          label={'Type'}
          register_key={'partner_type'}
          isRequired={true}
          register={register}
          type={'dropdown'}
          options={[
            { value: 'individual', label: 'Individual' },
            { value: 'company', label: 'Company' }
          ]}
          errors={errors.partner_type}
        />
        <FormInput
          width={48}
          label={'Phone Number'}
          register={register}
          isRequired={true}
          register_key={'phone_number'}
          type={'text'}
          onType={handlePhoneChange}
          errors={errors.phone_number}
        />
      </div>
      <div className="flex justify-between items-center w-full">
        {partnerType === 'individual' && (
          <FormInput
            width={48}
            label={'Full Name'}
            register_key={'name'}
            minLength={3}
            isRequired={true}
            register={register}
            type={'text'}
            onType={handleNameChange}
            errors={errors.name}
          />
        )}

        {partnerType === 'company' && (
          <FormInput
            width={48}
            label={'Business Name'}
            register_key={'businessName'}
            isRequired={true}
            register={register}
            type={'text'}
            errors={errors.businessName}
            onType={handleBusinessNameChange}
          />
        )}

        <FormInput
          width={48}
          label={'Email Address'}
          register={register}
          register_key={'email'}
          isRequired={true}
          type={'text'}
          errors={errors.email}
        />
      </div>
      <div className="flex justify-between items-center w-full">
        <FormInput
          width={48}
          register={register}
          isRequired
          register_key={'pan'}
          label={'PAN'}
          type={'text'}
          maxLength={10}
          errors={errors.pan}
          onType={handlePanChange}
          placeholder={'ABCDE1234F'}
        />

        {partnerType === 'individual' && (
          <FormInput
            width={48}
            label="Aadhar Number"
            isRequired
            register_key="aadhar"
            register={register}
            type="text"
            maxLength={12}
            onType={handleAadharChangeFirst}
            errors={errors.aadhar}
          />
        )}
        {partnerType === 'company' && (
          <FormInput
            width={48}
            label="GST"
            isRequired
            register_key="gst"
            register={register}
            type="text"
            maxLength={15}
            onType={handleGSTChange}
            errors={errors.gst}
            placeholder={'22ABCDE1234F1Z5'}
          />
        )}
      </div>

      <div className="w-full flex justify-between items-center">
        <div className="w-[48%]">
          <label className="text-gray-600 mb-1 block">Assign Company *</label>
          <Controller
            name="company_ids"
            control={control}
            render={({ field }) => (
              <Multiselect
                options={transformedDataCompany || []}
                selectedValues={field.value}
                onSelect={(selectedList) => {
                  console.log('Selected companies:', selectedList);
                  field.onChange(selectedList);
                }}
                onRemove={(selectedList) => {
                  console.log('Updated companies:', selectedList);
                  field.onChange(selectedList);
                }}
                displayValue="name"
                placeholder=""
                showCheckbox
              />
            )}
          />
          {errors.company_ids && <span className="text-red-500">{errors.company_ids.message}</span>}
        </div>
      </div>

      {/* Address Details */}
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Address Details</span>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between space-x-4">
          <FormInput
            width={48}
            register={register}
            isRequired
            register_key={'zip_code'}
            label={'Zip/Pin Code'}
            type={'text'}
            maxLength={6}
            errors={errors.zip_code}
            onType={handlePincodeChange}
          />
          <FormInput
            width={48}
            register={register}
            isRequired
            register_key={'country'}
            label={'Select Country'}
            valuePre={'India'}
            errors={errors.country}
            CustomStyle={{ pointerEvents: 'none' }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between space-x-4">
          <FormInput
            width={48}
            register={register}
            isRequired
            register_key={'state'}
            label={'State'}
            errors={errors.state}
            valuePre={state}
            CustomStyle={{ pointerEvents: 'none' }}
          />
          <FormInput
            width={48}
            register={register}
            isRequired
            register_key={'city'}
            label={'City'}
            defaultValuePre={city}
            type={'dropdown'}
            options={cityDropdownOptions}
            errors={errors.city}
          />
        </div>
      </div>

      {/* Form Action Buttons */}
      <div className="w-full flex justify-end items-center space-x-4 mt-8">
        <Button type="button" onClick={onClose} displayType="secondary">
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          Add
        </Button>
      </div>
    </form>
  );
};

export default AddPartner;
